
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Layer, Image } from '../../../../../react-konva';

import {
  StyledFilterItem,
  StyledFilterItemPreviewWrap,
  FilterItemPreview,
  FilterItemLabel,
  FILTER_CARD_PREVIEW_HEIGHT,
} from './Filters.styled';

const getPreviewLayout = (image, previewWidth, shownImageDimensions) => {
  if (!image || previewWidth <= 0) {
    return {
      previewWidth: 0,
      previewHeight: FILTER_CARD_PREVIEW_HEIGHT,
      imgW: 1,
      imgH: 1,
      scale: 1,
    };
  }

  const imgW =
    shownImageDimensions?.width ||
    image.naturalWidth ||
    image.width ||
    1;
  const imgH =
    shownImageDimensions?.height ||
    image.naturalHeight ||
    image.height ||
    1;
  const scale = previewWidth / imgW;
  const previewHeight = Math.max(1, Math.round(imgH * scale));

  return { previewWidth, previewHeight, imgW, imgH, scale };
};

const FilterItem = ({
  filterLabel,
  filterFn,
  applyFilter,
  isActive,
  image,
  shownImageDimensions,
}) => {
  const imageNodeRef = useRef();
  const previewWrapRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);

  const handleFilterApplying = useCallback(() => {
    applyFilter(filterFn);
  }, [applyFilter, filterFn]);

  const cacheImageNode = useCallback(() => {
    if (imageNodeRef.current) {
      imageNodeRef.current.cache();
    } else {
      setTimeout(cacheImageNode, 0);
    }
  }, []);

  useEffect(() => {
    const node = previewWrapRef.current;
    if (!node) {
      return undefined;
    }
    const measure = () => {
      setPreviewWidth(Math.max(0, Math.floor(node.clientWidth)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (image && previewWidth > 0) {
      cacheImageNode();
    }

    return () => {
      imageNodeRef.current?.clearCache();
    };
  }, [image, previewWidth, cacheImageNode]);

  const { previewHeight, imgW, imgH, scale } = getPreviewLayout(
    image,
    previewWidth,
    shownImageDimensions,
  );

  return (
    <StyledFilterItem
      type="button"
      className="respark_filters-item template-details effects-filter-card"
      onClick={handleFilterApplying}
      aria-selected={isActive}
      aria-label={filterLabel}
    >
      <StyledFilterItemPreviewWrap
        ref={previewWrapRef}
        className="respark_filters-item-preview-wrap"
        style={{ minHeight: previewHeight }}
      >
        {previewWidth > 0 ? (
          <FilterItemPreview
            className="respark_filters-item-preview"
            width={previewWidth}
            height={previewHeight}
          >
            <Layer onTap={handleFilterApplying}>
              <Image
                image={image}
                filters={filterFn ? [filterFn] : []}
                width={imgW}
                height={imgH}
                scaleX={scale}
                scaleY={scale}
                x={0}
                y={0}
                ref={imageNodeRef}
              />
            </Layer>
          </FilterItemPreview>
        ) : (
          <div
            className="respark_filters-item-preview respark_filters-item-preview-placeholder"
            style={{
              width: '100%',
              height: previewHeight,
              minHeight: 80,
            }}
          />
        )}
      </StyledFilterItemPreviewWrap>
      <FilterItemLabel className="respark_filters-item-label">
        {filterLabel}
      </FilterItemLabel>
    </StyledFilterItem>
  );
};

FilterItem.propTypes = {
  image: PropTypes.instanceOf(HTMLImageElement).isRequired,
  shownImageDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  filterLabel: PropTypes.string.isRequired,
  filterFn: PropTypes.func,
  applyFilter: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
};

export default memo(FilterItem);
